import { useTranslation } from 'react-i18next'
import { usePage } from '@inertiajs/react'
import { Home, RefreshCw, ArrowLeft } from 'lucide-react'
import AppLayout from '@components/layout/Layout/AppLayout'

interface ErrorPageProps {
  status: number
  title: string
  message: string
  showHomeButton?: boolean
  showRetryButton?: boolean
  showBackButton?: boolean
  onRetry?: () => void
}

export default function ErrorPage({
  status,
  title,
  message,
  showHomeButton = true,
  showRetryButton = false,
  showBackButton = false,
  onRetry
}: ErrorPageProps) {
  const { t } = useTranslation()
  const { user } = usePage<{ user: any }>().props

  const handleRetry = () => {
    if (onRetry) {
      onRetry()
    } else {
      window.location.reload()
    }
  }

  const handleBack = () => {
    window.history.back()
  }

  return (
    <AppLayout user={user}>
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-4">
        <div className="text-center max-w-2xl">
          {/* Status Code */}
          <h1 className="text-9xl font-bold text-primary mb-4">
            {status}
          </h1>

          {/* Title */}
          <h2 className="text-3xl font-semibold text-base-content mb-4">
            {title}
          </h2>

          {/* Message */}
          <p className="text-lg text-base-content/70 mb-8">
            {message}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 justify-center">
            {showBackButton && (
              <button
                onClick={handleBack}
                className="btn btn-ghost gap-2"
              >
                <ArrowLeft className="w-5 h-5" />
                {t('common.goBack') || 'Go Back'}
              </button>
            )}

            {showRetryButton && (
              <button
                onClick={handleRetry}
                className="btn btn-primary gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                {t('errors.actions.retry') || 'Try Again'}
              </button>
            )}

            {showHomeButton && (
              <a
                href="/"
                className="btn btn-primary gap-2"
              >
                <Home className="w-5 h-5" />
                {t('common.backToHome') || 'Back to Home'}
              </a>
            )}
          </div>

          {/* Additional Help Text */}
          {status === 500 && (
            <p className="text-sm text-base-content/50 mt-8">
              {t('errors.pages.500.helpText') || 'If this problem persists, please contact support.'}
            </p>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
